import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from '@/globalContext';
import { dataCollectorAvailable } from '@models/env';
import {
    AgeNestedData,
    Endpoint,
    Liveness,
    OperatorField,
    PredictionApiData,
    PredictionResponse,
    RequestBody,
    SecureMode,
    MultiframeMode,
} from '@models/prediction';
import { isApiError } from '@api/base';
import Config from '@utils/config';
import { buildUrl } from '@utils/url';
import useApi from './useApi';

/**
 * Transforms 'res' in the custom prediction result for the 'endpoint' provided.
 * 'res' is typed as 'unknown' at the boundary since response shapes vary per endpoint.
 */
const getPredictionResult = (
    res: unknown,
    endpoint: Endpoint,
    error?: AxiosError,
): PredictionResponse | undefined => {
    if (res) {
        const data = res as PredictionApiData;
        switch (endpoint) {
            case Endpoint.Age:
                if (typeof data.age !== 'number' || typeof data.st_dev !== 'number') {
                    break;
                }
                return { age: data.age, stDev: data.st_dev };
            case Endpoint.Antispoofing:
                if (
                    data.prediction === undefined ||
                    !Object.values(Liveness).includes(data.prediction)
                ) {
                    break;
                }
                return { prediction: data.prediction };
            case Endpoint.AgeAntispoofing: {
                const ageNested = data.age as AgeNestedData | undefined;
                if (
                    typeof ageNested?.age !== 'number' ||
                    typeof ageNested?.st_dev !== 'number' ||
                    data.antispoofing?.prediction === undefined ||
                    !Object.values(Liveness).includes(data.antispoofing.prediction)
                ) {
                    break;
                }
                return {
                    prediction: data.antispoofing.prediction,
                    age: ageNested.age,
                    stDev: ageNested.st_dev,
                };
            }
            case Endpoint.AgeAntispoofingVerify: {
                const ageNested = data.age as AgeNestedData | undefined;
                if (
                    ageNested?.age_check === undefined ||
                    data.antispoofing?.prediction === undefined ||
                    !Object.values(Liveness).includes(data.antispoofing.prediction)
                ) {
                    break;
                }
                return {
                    ageCheck: ageNested.age_check,
                    prediction: data.antispoofing.prediction,
                };
            }
            case Endpoint.AgeVerify:
                if (
                    data.age_check === undefined ||
                    !Object.values(Liveness).includes(data.age_check)
                ) {
                    break;
                }
                return { ageCheck: data.age_check };
            case Endpoint.SelfCheckoutAntiSpoofing: {
                const ageNested = data.age as AgeNestedData | undefined;
                if (
                    typeof ageNested?.age !== 'number' ||
                    typeof ageNested?.st_dev !== 'number' ||
                    data.antispoofing?.prediction === undefined ||
                    !Object.values(Liveness).includes(data.antispoofing.prediction)
                ) {
                    break;
                }
                return {
                    prediction: data.antispoofing.prediction,
                    age: ageNested.age,
                    stDev: ageNested.st_dev,
                };
            }
        }
    }

    // Check for API error (i.e. validation error).
    if (error && isApiError(error)) {
        return {
            errorMessage: error.response.data.error_message,
            errorCode: error.response.data.error_code,
        };
    }
};

export const unexpectedErr = new Error('Unexpected response');
const apiBaseUrl = Config.apiUrl.replace('/api', '');

interface State {
    prediction?: PredictionResponse;
    error?: Error;
}

export interface RequestData {
    threshold?: number;
    operator?: OperatorField;
}

/**
 * The hook performs the prediction request for the provided 'endpoint'. It
 * handle the API errors from server side to transform them to a prediction
 * response (i.e. validation error). The 'error' state indicates there is an
 * unhandled error in the request or response.
 */
export default function usePredict(
    endpoint: Endpoint,
    secure: SecureMode,
    multiframe: MultiframeMode,
) {
    const { scanConfiguration, account, envConfig } = useGlobalContext();
    let url = `${endpoint}`;
    if (secure === SecureMode.Secure) {
        url += '?secure=true';
    }
    if (secure === SecureMode.Secure && multiframe === MultiframeMode.Multiframe) {
        url += '&multiframe=true';
    }
    const apiRes = useApi(buildUrl(apiBaseUrl, url), {
        hideErrorNotification: () => true,
    });
    const { data, headers: resHeaders, call, error } = apiRes;
    const [state, setState] = useState<State>({});
    const [request, setRequest] = useState<RequestData>();

    // Handle valid response (the prediction run).
    useEffect(() => {
        const res = getPredictionResult(data, endpoint, error);

        // Set the log id if it's present in the response headers.
        if (res) {
            const headerLogId = resHeaders ? `${resHeaders['x-prediction-id']}` : '';
            const logId = parseInt(headerLogId, 10);
            if (!isNaN(logId)) {
                res.logId = logId;
            }
        }

        // Set the error state if the api returns an error that is not handled
        // as a valid prediction result (including validation errors).
        const apiError = !res && error ? unexpectedErr : undefined;
        setState({ prediction: res, error: apiError });
    }, [data, resHeaders, endpoint, error]);

    const headers = useMemo(() => {
        const headers: Record<string, string> = {};
        if (dataCollectorAvailable(account?.role, envConfig?.collectorFields)) {
            if (scanConfiguration.selectedExpectedResult) {
                headers['X-Expected-Result'] = scanConfiguration.selectedExpectedResult;
            }
            if (scanConfiguration.selectedExpectedLocation) {
                headers['X-Expected-Location'] = scanConfiguration.selectedExpectedLocation;
            }
            if (scanConfiguration.selectedExpectedBrightness) {
                headers['X-Expected-Brightness'] = scanConfiguration.selectedExpectedBrightness;
            }
            if (
                scanConfiguration.selectedDeceptionTypes &&
                scanConfiguration.selectedDeceptionTypes.length > 0
            ) {
                headers['X-Deception-Type'] = JSON.stringify(
                    scanConfiguration.selectedDeceptionTypes,
                );
            }
        }
        return headers;
    }, [
        scanConfiguration.selectedExpectedResult,
        scanConfiguration.selectedExpectedLocation,
        scanConfiguration.selectedExpectedBrightness,
        scanConfiguration.selectedDeceptionTypes,
        account?.role,
        envConfig?.collectorFields,
    ]);

    const predict = useCallback(
        (data: RequestBody) => {
            setRequest((prev) => ({ ...prev, threshold: data.threshold, operator: data.operator }));
            setState({});
            call({
                method: 'POST',
                data,
                headers: {
                    ...headers,
                },
            });
        },
        [call, headers],
    );

    return {
        ...state,
        predict,
        abort: apiRes.abort,
        request,
    };
}
